import type {
  Agent,
  AgentSubscriptionPlan as AgentSubscriptionPlanPrisma,
  InsuranceScenarioKey,
  Tx,
  TxStatus as TxStatusPrisma,
  TxType as TxTypePrisma,
} from '@/backend/src/services/other/prisma.js'
import { checkEnumEq } from '@/general/src/other/checkEnumEq.js'
import { getConstKeys } from '@/general/src/other/getConstKeys.js'
import { toMoney } from '@/general/src/other/money.js'
import type { AgentSubscriptionPlanMap, ScenarioTokenCostMap } from '@/general/src/subscription/utils.server.js'
import { formatDate } from 'date-fns/format'
import { z } from 'zod'

export type SubscriptionPlanDataShared = {
  name: AgentSubscriptionPlanPrisma
  label: string
}
export type SubscriptionPlanData = {
  name: AgentSubscriptionPlanPrisma
  label: string
  pricePerPeriod: number
  temporaryScenarioTokensCountPerPeriod: number
  temporaryRecognitionTokensCountPerPeriod: number
  refreshPeriodDuration: '1month' | '1day' | '10minutes'
  retryPeriodDuration: '1day' | '1hour' | '10minutes' | '1minute'
  maxRefreshTriesCount: number
}
export type PaidSubscriptionPlanData = Omit<SubscriptionPlanData, 'name'> & {
  name: AgentPaidSubscriptionPlan
}
export const agentSubscriptionPlanMapShared: Record<AgentSubscriptionPlanPrisma, SubscriptionPlanDataShared> = {
  free: {
    name: 'free',
    label: 'Бесплатный',
  },
  basic: {
    name: 'basic',
    label: 'Базовый',
  },
}
export const agentSubscriptionPlans = getConstKeys(agentSubscriptionPlanMapShared)
export const agentPaidSubscriptionPlans = ['basic'] as const
export const zAgentSubscriptionPlan = z.enum(agentSubscriptionPlans)
export const zAgentPaidSubscriptionPlan = z.enum(agentPaidSubscriptionPlans)
export type AgentSubscriptionPlan = (typeof agentSubscriptionPlans)[number]
export type AgentPaidSubscriptionPlan = (typeof agentPaidSubscriptionPlans)[number]
checkEnumEq<AgentSubscriptionPlan, AgentSubscriptionPlanPrisma, true>()
export const getSubscriptionPlanLabel = (subscriptionPlan: AgentSubscriptionPlan) => {
  return agentSubscriptionPlanMapShared[subscriptionPlan].label
}

export const txTypeMap = {
  subscription: 'Покупка подписки',
  permanentRecognition: 'Покупка несгораемых токенов распознования документов',
  permanentScenario: 'Покупка несгораемых токенов для сценариев',
  addBankCard: 'Добавление банковской карты',
} as const
export const zTxType = z.enum(getConstKeys(txTypeMap))
export type TxType = keyof typeof txTypeMap
checkEnumEq<TxType, TxTypePrisma, true>()
export const getTxTypeLabel = (txType: TxType) => {
  return txTypeMap[txType]
}

export const txStatusMap = {
  success: 'Успешно',
  failed: 'Неуспешно',
  refunded: 'Возвращено',
} as const
export type TxStatus = keyof typeof txStatusMap
checkEnumEq<TxStatus, TxStatusPrisma, true>()
export const getTxStatusLabel = (txStatus: TxStatus) => {
  return txStatusMap[txStatus]
}

export const zSubscriptionTxData = z.object({
  actorType: z.enum(['agent', 'system']),
  type: z.literal('subscription'),
  subscriptionPlan: zAgentPaidSubscriptionPlan,
})
export type SubscriptionTxData = z.infer<typeof zSubscriptionTxData>

export const zPermanentRecognitionTxData = z.object({
  actorType: z.literal('agent'),
  type: z.literal('permanentRecognition'),
  permanentRecognitionTokensCount: z.coerce.number().int(),
})
export type PermanentRecognitionTxData = z.infer<typeof zPermanentRecognitionTxData>

export const zPermanentScenarioTxData = z.object({
  actorType: z.literal('agent'),
  type: z.literal('permanentScenario'),
  permanentScenarioTokensCount: z.coerce.number().int(),
})
export type PermanentScenarioTxData = z.infer<typeof zPermanentScenarioTxData>

export const zAddBankCardTxData = z.object({
  actorType: z.literal('agent'),
  type: z.literal('addBankCard'),
})
export type AddBankCardTxData = z.infer<typeof zAddBankCardTxData>

export const zTxData = z.union([
  zSubscriptionTxData,
  zPermanentRecognitionTxData,
  zPermanentScenarioTxData,
  zAddBankCardTxData,
])
export type TxData = z.infer<typeof zTxData>

export const getTxDescription = (
  tx: Partial<
    Pick<
      Tx,
      | 'type'
      | 'status'
      | 'amount'
      | 'subscriptionPlan'
      | 'permanentRecognitionTokensCount'
      | 'permanentScenarioTokensCount'
    >
  >
) => {
  if (tx.type === 'subscription' && tx.subscriptionPlan) {
    return `${getTxTypeLabel(tx.type)} на тарифе «${getSubscriptionPlanLabel(tx.subscriptionPlan)}»`
  } else if (tx.type === 'permanentRecognition' && tx.permanentRecognitionTokensCount) {
    return `${getTxTypeLabel(tx.type)} в количестве ${tx.permanentRecognitionTokensCount}`
  } else if (tx.type === 'permanentScenario' && tx.permanentScenarioTokensCount) {
    return `${getTxTypeLabel(tx.type)} в количестве ${tx.permanentScenarioTokensCount}`
  } else if (tx.type === 'addBankCard') {
    return getTxTypeLabel(tx.type)
  } else {
    return 'Неизвестное назначение, пожалуйста, обратитесь в поддержку'
  }
}

export const getSubscriptionPlanData = ({
  agentSubscriptionPlanMap,
  subscriptionPlan,
}: {
  agentSubscriptionPlanMap: AgentSubscriptionPlanMap
  subscriptionPlan: AgentSubscriptionPlan
}) => {
  return agentSubscriptionPlanMap[subscriptionPlan]
}

export type AgentSubscriptionData = Pick<
  Agent,
  | 'subscriptionPlan'
  | 'freeSubscriptionPlanWillRefreshAt'
  | 'paidSubscriptionPlanExpiresAt'
  | 'shouldRefreshPaidSubscriptionPlan'
  | 'nextPaidSubscriptionPlanRefreshTryWillAt'
  | 'lastPaidSubscriptionPlanRefreshFailedTryWasAt'
  | 'lastPaidSubscriptionPlanRefreshFailedTryErrorMessage'
  | 'paidSubscriptionPlanRefreshFailedTriesCount'
  | 'permanentRecognitionTokensCount'
  | 'temporaryRecognitionTokensCount'
  | 'permanentScenarioTokensCount'
  | 'temporaryScenarioTokensCount'
>

export const getAgentSubscriptionMessage = ({
  agentSubscriptionPlanMap,
  subscriptionData,
}: {
  agentSubscriptionPlanMap: AgentSubscriptionPlanMap
  subscriptionData: AgentSubscriptionData
}) => {
  const {
    subscriptionPlan,
    freeSubscriptionPlanWillRefreshAt,
    lastPaidSubscriptionPlanRefreshFailedTryWasAt,
    paidSubscriptionPlanExpiresAt,
    nextPaidSubscriptionPlanRefreshTryWillAt,
    paidSubscriptionPlanRefreshFailedTriesCount,
    shouldRefreshPaidSubscriptionPlan,
    permanentRecognitionTokensCount,
    permanentScenarioTokensCount,
  } = subscriptionData
  const subscriptionPlanData = getSubscriptionPlanData({ agentSubscriptionPlanMap, subscriptionPlan })
  const {
    temporaryRecognitionTokensCountPerPeriod,
    temporaryScenarioTokensCountPerPeriod,
    pricePerPeriod,
    label,
    name,
  } = subscriptionPlanData
  const planFullString = `Ваш текущий тариф: «${label}»`
  const nextTokensCountString = `количество токенов обновится — токены распознования документов: ${temporaryRecognitionTokensCountPerPeriod}, токены для сценариев: ${temporaryScenarioTokensCountPerPeriod}`
  const pricePerPeriodString = `с вашей карты будет списано ${toMoney({ amount: pricePerPeriod })}`
  const permanentTokensCountString =
    permanentRecognitionTokensCount || permanentScenarioTokensCount
      ? `Несгораемые токены никуда не денутся и будут тратиться только после того, как вы потратите все обычные токены`
      : ''
  const formatDateHere = (date: Date | null | undefined) => formatDate(date || new Date(), 'dd.MM.yyyy HH:mm')
  const mergeStrings = (strings: string[]) => strings.filter(Boolean).join('. ') + '.'
  if (name === 'free') {
    return mergeStrings([
      planFullString,
      `${formatDateHere(freeSubscriptionPlanWillRefreshAt)} ${nextTokensCountString}`,
      permanentTokensCountString,
    ])
  } else {
    if (!lastPaidSubscriptionPlanRefreshFailedTryWasAt) {
      if (shouldRefreshPaidSubscriptionPlan) {
        return mergeStrings([
          planFullString,
          `${formatDateHere(paidSubscriptionPlanExpiresAt)} ${pricePerPeriodString}, ${nextTokensCountString}`,
          permanentTokensCountString,
        ])
      } else {
        return mergeStrings([
          planFullString,
          `${formatDateHere(paidSubscriptionPlanExpiresAt)} ваша подписка закончится и не будет продлена, вы перейдёте на бесплатный тариф`,
          permanentTokensCountString,
        ])
      }
    } else {
      const leftPaidSubscriptionPlanRefreshTriesCount =
        subscriptionPlanData.maxRefreshTriesCount - paidSubscriptionPlanRefreshFailedTriesCount
      return mergeStrings([
        planFullString,
        `${formatDateHere(lastPaidSubscriptionPlanRefreshFailedTryWasAt)} была совершена попытка списать с вашей карты ${toMoney({ amount: pricePerPeriod })} для продления подписки, но не получилось. Мы повторим попытку ${formatDateHere(nextPaidSubscriptionPlanRefreshTryWillAt)}. Будем пытаться списать деньги в течение следующих ${leftPaidSubscriptionPlanRefreshTriesCount} дней, если не получится, то переведём вас на бесплатный тариф`,
        `После успешной оплаты ваша подписка будет продлена и ${nextTokensCountString}`,
        permanentTokensCountString,
      ])
    }
  }
}

export const getInsuranceScenarioTokenCost = ({
  scenarioTokenCostMap,
  insuranceScenarioKey,
}: {
  scenarioTokenCostMap: ScenarioTokenCostMap
  insuranceScenarioKey: InsuranceScenarioKey
}) => {
  return scenarioTokenCostMap[insuranceScenarioKey]
}

export const getInsuranceCampaignScenarioTokenCostByScenarioKeys = ({
  scenarioTokenCostMap,
  insuranceScenarioKeys,
}: {
  scenarioTokenCostMap: ScenarioTokenCostMap
  insuranceScenarioKeys: InsuranceScenarioKey[]
}) => {
  return insuranceScenarioKeys.reduce(
    (acc, insuranceScenarioKey) => acc + getInsuranceScenarioTokenCost({ scenarioTokenCostMap, insuranceScenarioKey }),
    0
  )
}
